import type { ClassId } from '../shared/types';
import type {
  ClientMessage,
  GameEvent,
  LeaderboardEntry,
  ServerMessage,
  SocialState,
} from '../shared/protocol';

export type NetStatus = 'idle' | 'connecting' | 'playing' | 'closed';
const PING_INTERVAL_MS = 2000;
const MAX_QUEUE = 120;

export class Net {
  status: NetStatus = 'idle';
  /** Our own entity id, assigned by the server. */
  selfId = 0;
  online = 1;
  ping = 0;
  lastError = '';

  private socket: WebSocket | null = null;
  private queue: ServerMessage[] = [];
  private pingTimer: number | null = null;
  /** True when we closed on purpose, so the close handler stays quiet. */
  intentionalClose = false;

  onWelcome: ((welcome: Extract<ServerMessage, { t: 'welcome' }>) => void) | null = null;
  onEvent: ((event: GameEvent) => void) | null = null;
  onClosed: (() => void) | null = null;
  onError: ((message: string) => void) | null = null;
  onNotice: ((message: string, kind: 'info' | 'warn') => void) | null = null;
  onLoadout: ((msg: Extract<ServerMessage, { t: 'loadout' }>) => void) | null = null;
  onSocial: ((state: SocialState) => void) | null = null;
  onLeaderboard: ((entries: LeaderboardEntry[]) => void) | null = null;

  get connected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  connect(name: string, classId: ClassId): void {
    this.disconnect();
    this.intentionalClose = false;
    this.status = 'connecting';
    this.lastError = '';

    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(`${protocol}://${location.host}`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.send({ t: 'join', name, classId });
    });

    socket.addEventListener('message', (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      if (!msg || typeof msg !== 'object') return;

      switch (msg.t) {
        case 'welcome':
          this.selfId = msg.id;
          this.status = 'playing';
          this.onWelcome?.(msg);
          this.queue.push(msg);
          break;
        case 'state':
          this.queue.push(msg);
          break;
        case 'pong':
          this.ping = Math.max(0, Math.round(performance.now() - msg.time));
          this.online = msg.players;
          break;
        case 'error':
          this.lastError = msg.message;
          this.onError?.(msg.message);
          break;
        case 'notice':
          this.onNotice?.(msg.message, msg.kind);
          break;
        case 'loadout':
          this.onLoadout?.(msg);
          break;
        case 'social':
          this.onSocial?.(msg.state);
          break;
        case 'leaderboard':
          this.onLeaderboard?.(msg.entries);
          break;
        default:
          break;
      }
      if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
    });

    socket.addEventListener('close', () => {
      if (this.status === 'playing') {
        this.status = 'closed';
        this.onClosed?.();
      } else if (this.status === 'connecting') {
        this.status = 'closed';
        this.onError?.('Could not reach the server.');
      }
      this.stopPing();
    });

    socket.addEventListener('error', () => {
      /* the close handler reports the failure */
    });

    this.startPing();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ t: 'ping', time: performance.now() });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  send(message: ClientMessage): void {
    if (!this.connected) return;
    try {
      this.socket!.send(JSON.stringify(message));
    } catch {
      /* ignore - the close handler will clean up */
    }
  }

  /** Hand the queued server messages to the caller and clear the queue. */
  drain(): ServerMessage[] {
    if (this.queue.length === 0) return [];
    const batch = this.queue;
    this.queue = [];
    return batch;
  }

  disconnect(): void {
    this.stopPing();
    if (this.socket) {
      this.socket.onclose = null;
      try {
        this.socket.close();
      } catch {
        /* already closed */
      }
      this.socket = null;
    }
  }

  /**
   * Leaves the world on purpose - back to the character select screen. The
   * server still saves and removes the character; we just do not treat the
   * closed socket as a connection failure.
   */
  logout(): void {
    this.intentionalClose = true;
    this.disconnect();
    this.status = 'closed';
    this.onClosed?.();
  }
}
